import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Grid, Tabs, Tab, Button,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, Divider, Stack, Alert, TextField, CircularProgress, LinearProgress,
    Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Avatar, MenuItem
} from '@mui/material';
import {
    ReceiptLong as ReceiptLongIcon,
    School as SchoolIcon,
    Today as TodayIcon,
    CheckCircle as PresentIcon,
    Cancel as AbsentIcon,
    WatchLater as LateIcon,
    EventNote as TimetableIcon,
    AccessTime as AccessTimeIcon,
    MenuBook as MenuBookIcon,
    Print as PrintIcon,
    Close as CloseIcon,
    Download as DownloadIcon,
    Assessment as AssessmentIcon
} from '@mui/icons-material';
import LockIcon from '@mui/icons-material/Lock';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import EventsWidget from '../components/Dashboard/EventsWidget';
import { getApiUrl, getUploadUrl } from '../config/api';
import { getLetterGrade } from '../utils/gradeUtils';

const ParentPortalPage = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(0);
    const [students, setStudents] = useState<any[]>([]);
    const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
    const [user, setUser] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [attendanceData, setAttendanceData] = useState<any>(null);
    const [attendanceLoading, setAttendanceLoading] = useState(false);
    const [timetableData, setTimetableData] = useState<any[]>([]);
    const [timetableLoading, setTimetableLoading] = useState(false);
    const [reportCardData, setReportCardData] = useState<any>(null);
    const [reportCardLoading, setReportCardLoading] = useState(false);
    const [reportCardDialogOpen, setReportCardDialogOpen] = useState(false);
    const [selectedTerm, setSelectedTerm] = useState('Term 1');
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [school, setSchool] = useState<any>(null);
    const reportRef = React.useRef<HTMLDivElement>(null);

    // Password State
    const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
    const [passMessage, setPassMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        const fetchParentData = async () => {
            try {
                const userInfo = localStorage.getItem('userInfo');
                if (!userInfo) return;
                const parsedUser = JSON.parse(userInfo);
                const token = parsedUser.token;
                setUser(parsedUser.user || parsedUser);

                const config = { headers: { Authorization: `Bearer ${token}` } };
                // Fetch linked students
                const [studentRes, schoolRes] = await Promise.all([
                    axios.get(getApiUrl('parents/students'), config),
                    axios.get(getApiUrl('school'), config)
                ]);
                
                setStudents(studentRes.data);
                setSchool(schoolRes.data);
                
                if (studentRes.data.length > 0) {
                    setSelectedStudentId(studentRes.data[0].id);
                }
            } catch (error) {
                console.error('Error fetching parent data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchParentData();
    }, []);

    // Fetch student attendance when tab 2 is selected
    useEffect(() => {
        if (activeTab === 2 && selectedStudentId) {
            const fetchAttendance = async () => {
                try {
                    setAttendanceLoading(true);
                    const userInfo = localStorage.getItem('userInfo');
                    const token = userInfo ? JSON.parse(userInfo).token : null;
                    const res = await axios.get(
                        getApiUrl(`attendance-records/student/${selectedStudentId}`),
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                    setAttendanceData(res.data);
                } catch (err) {
                    console.error('Attendance fetch failed:', err);
                } finally {
                    setAttendanceLoading(false);
                }
            };
            fetchAttendance();
        }
    }, [activeTab, selectedStudentId]);

    // Fetch timetable when tab 3 is selected
    useEffect(() => {
        const selectedStudent = students.find((s: any) => s.id === selectedStudentId);
        if (activeTab === 3 && selectedStudent?.classGroupId) {
            const fetchTimetable = async () => {
                try {
                    setTimetableLoading(true);
                    const userInfo = localStorage.getItem('userInfo');
                    const token = userInfo ? JSON.parse(userInfo).token : null;
                    const res = await axios.get(
                        getApiUrl(`timetable?classGroupId=${selectedStudent.classGroupId}`),
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                    setTimetableData(res.data || []);
                } catch (err) {
                    console.error('Timetable fetch failed:', err);
                } finally {
                    setTimetableLoading(false);
                }
            };
            fetchTimetable();
        }
    }, [activeTab, selectedStudentId, students]);

    // Fetch report card when tab 1 is selected
    useEffect(() => {
        if (activeTab === 1 && selectedStudentId) {
            const fetchReportCard = async () => {
                try {
                    setReportCardLoading(true);
                    const userInfo = localStorage.getItem('userInfo');
                    const token = userInfo ? JSON.parse(userInfo).token : null;
                    const res = await axios.get(
                        getApiUrl(`grades/report-card/${selectedStudentId}`),
                        { 
                            params: { term: selectedTerm, academicYear: selectedYear },
                            headers: { Authorization: `Bearer ${token}` } 
                        }
                    );
                    setReportCardData(res.data);
                } catch (err) {
                    console.error('Report card fetch failed:', err);
                } finally {
                    setReportCardLoading(false);
                }
            };
            fetchReportCard();
        }
    }, [activeTab, selectedStudentId, selectedTerm, selectedYear]);

    const selectedStudent = students.find(s => s.id === selectedStudentId);
    const householdBalance = students.reduce((sum, s) => {
        const invoiced = s.invoices?.reduce((acc: number, inv: any) => acc + Number(inv.totalAmount), 0) || 0;
        const paid = s.invoices?.reduce((acc: number, inv: any) => acc + (inv.receipts?.reduce((rAcc: number, r: any) => rAcc + Number(r.amount), 0) || 0), 0) || 0;
        return sum + (invoiced - paid);
    }, 0);

    const handleChangePassword = async () => {
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setPassMessage({ type: 'error', text: 'New passwords do not match' });
            return;
        }
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            await axios.put(getApiUrl('auth/update-password'),
                { currentPassword: passwordData.currentPassword, newPassword: passwordData.newPassword },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setPassMessage({ type: 'success', text: 'Password updated successfully' });
            setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (error: any) {
            setPassMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update password' });
        }
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    if (loading) return <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#F4F7FE', minHeight: 'calc(100vh - 64px)' }}>
            {/* Header & Household Summary */}
            <Grid container spacing={3} mb={4} alignItems="center">
                <Grid size={{ xs: 12, md: 7 }}>
                    <Typography variant="h4" fontWeight={900} color="#263B7C">Parent Portal</Typography>
                    <Typography variant="body1" color="text.secondary" fontWeight={500}>
                        Welcome back, {user?.name}. Overseeing {students.length} {students.length === 1 ? 'student' : 'students'}.
                    </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 5 }}>
                    <Paper elevation={0} sx={{
                        p: 2.5,
                        borderRadius: '20px',
                        bgcolor: '#263B7C',
                        color: 'white',
                        boxShadow: '0 10px 30px rgba(38, 59, 124, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <Box>
                            <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>Household Balance</Typography>
                            <Typography variant="h4" fontWeight={900}>${householdBalance.toLocaleString()}</Typography>
                        </Box>
                        <ReceiptLongIcon sx={{ fontSize: 40, opacity: 0.3 }} />
                    </Paper>
                </Grid>
            </Grid>

            {/* Student Selector */}
            {students.length > 1 && (
                <Stack direction="row" spacing={2} mb={4} sx={{ overflowX: 'auto', pb: 1 }}>
                    {students.map((student) => (
                        <Box
                            key={student.id}
                            onClick={() => setSelectedStudentId(student.id)}
                            sx={{
                                minWidth: 200,
                                p: 2,
                                borderRadius: '16px',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                border: '2px solid',
                                borderColor: selectedStudentId === student.id ? '#F9B43F' : '#edf2f7',
                                bgcolor: selectedStudentId === student.id ? 'white' : 'transparent',
                                boxShadow: selectedStudentId === student.id ? '0 10px 20px rgba(0,0,0,0.05)' : 'none',
                                '&:hover': { borderColor: '#F9B43F' }
                            }}
                        >
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Box sx={{
                                    width: 40, height: 40, borderRadius: '12px', bgcolor: '#F4F7FE',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#263B7C'
                                }}>
                                    <SchoolIcon />
                                </Box>
                                <Box>
                                    <Typography variant="subtitle2" fontWeight={800} noWrap>{student.firstName}</Typography>
                                    <Typography variant="caption" color="text.secondary" fontWeight={700}>{student.grade}</Typography>
                                </Box>
                            </Stack>
                        </Box>
                    ))}
                </Stack>
            )}

            <Paper elevation={0} sx={{ borderRadius: '24px', border: '1px solid #edf2f7', overflow: 'hidden', bgcolor: 'white' }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    sx={{ borderBottom: 1, borderColor: 'divider', px: 3, pt: 2 }}
                    TabIndicatorProps={{ sx: { bgcolor: '#F9B43F', height: 4, borderRadius: '4px 4px 0 0' } }}
                >
                    <Tab icon={<ReceiptLongIcon />} iconPosition="start" label="FINANCIALS" sx={{ fontWeight: 800, fontSize: '0.8rem' }} />
                    <Tab icon={<SchoolIcon />} iconPosition="start" label="ACADEMICS" sx={{ fontWeight: 800, fontSize: '0.8rem' }} />
                    <Tab icon={<TodayIcon />} iconPosition="start" label="ATTENDANCE" sx={{ fontWeight: 800, fontSize: '0.8rem' }} />
                    <Tab icon={<TimetableIcon />} iconPosition="start" label="TIMETABLE" sx={{ fontWeight: 800, fontSize: '0.8rem' }} />
                    <Tab icon={<LockIcon />} iconPosition="start" label="SECURITY" sx={{ fontWeight: 800, fontSize: '0.8rem' }} />
                </Tabs>

                <Box sx={{ p: { xs: 2, md: 4 } }}>
                    {selectedStudent ? (
                        <>
                            {/* Report Card Dialog */}
                            <Dialog
                                open={reportCardDialogOpen}
                                onClose={() => setReportCardDialogOpen(false)}
                                maxWidth="lg"
                                fullWidth
                                PaperProps={{
                                    sx: { borderRadius: '24px', overflow: 'hidden' }
                                }}
                            >
                                <style>
                                    {`
                                        @media print {
                                            body * { visibility: hidden; }
                                            #report-card-printable, #report-card-printable * { visibility: visible; }
                                            #report-card-printable {
                                                position: absolute;
                                                left: 0;
                                                top: 0;
                                                width: 100%;
                                                padding: 0;
                                                margin: 0;
                                                background: white !important;
                                                print-color-adjust: exact;
                                                -webkit-print-color-adjust: exact;
                                            }
                                            .no-print { display: none !important; }
                                            .MuiPaper-root { border: 1px solid #edf2f7 !important; box-shadow: none !important; }
                                            @page { size: auto; margin: 15mm; }
                                        }
                                    `}
                                </style>
                                <DialogTitle sx={{ bgcolor: '#263B7C', color: 'white', p: 3 }} className="no-print">
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Box>
                                            <Typography variant="h6" fontWeight={900}>OFFICIAL STUDENT REPORT CARD</Typography>
                                            <Typography variant="caption" sx={{ opacity: 0.8 }}>Academic Year {selectedYear} - {selectedTerm}</Typography>
                                        </Box>
                                        <Stack direction="row" spacing={1}>
                                            <Button startIcon={<PrintIcon />} sx={{ color: 'white' }} onClick={() => window.print()}>Print</Button>
                                            <IconButton onClick={() => setReportCardDialogOpen(false)} sx={{ color: 'white' }}><CloseIcon /></IconButton>
                                        </Stack>
                                    </Stack>
                                </DialogTitle>
                                <DialogContent sx={{ p: 4 }}>
                                    {reportCardLoading ? (
                                        <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress /></Box>
                                    ) : reportCardData ? (
                                        <Box ref={reportRef} id="report-card-printable" sx={{ bgcolor: 'white', pb: 10 }}>
                                            {/* Professional Branded Header */}
                                            <Grid container spacing={4} sx={{ mb: 6, pb: 4, borderBottom: '2px solid #263B7C' }}>
                                                <Grid size={{ xs: 12, md: 7 }}>
                                                    <Stack direction="row" spacing={3} alignItems="center">
                                                        {school?.logo && (
                                                            <Box
                                                                component="img"
                                                                src={getUploadUrl(school.logo)}
                                                                sx={{ height: 100, width: 100, objectFit: 'contain' }}
                                                            />
                                                        )}
                                                        <Box>
                                                            <Typography variant="h5" fontWeight={900} color="#263B7C" sx={{ letterSpacing: -0.5 }}>
                                                                {school?.name || 'School Management System'}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ maxWidth: 400, lineHeight: 1.4 }}>
                                                                {school?.address}
                                                                {'\n'}{school?.phone} | {school?.email}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                </Grid>
                                                <Grid size={{ xs: 12, md: 5 }} sx={{ textAlign: 'right' }}>
                                                    <Typography variant="h4" fontWeight={900} color="#F1F4F9" sx={{ letterSpacing: 2, mb: -1, opacity: 0.5 }}>
                                                        REPORT CARD
                                                    </Typography>
                                                    <Typography variant="h6" fontWeight={900} color="#263B7C">
                                                        TERM END RESULTS
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight={800} color="#F9B43F">
                                                        {selectedYear} | {selectedTerm}
                                                    </Typography>
                                                </Grid>
                                            </Grid>

                                            {/* Student Header */}
                                            <Grid container spacing={3} sx={{ mb: 4 }}>
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    <Paper variant="outlined" sx={{ p: 3, borderRadius: '16px', bgcolor: '#F8FAFC' }}>
                                                        <Stack direction="row" spacing={3} alignItems="center">
                                                            <Avatar sx={{ width: 80, height: 80, fontSize: '2rem', bgcolor: '#263B7C' }}>
                                                                {selectedStudent.firstName[0]}{selectedStudent.lastName[0]}
                                                            </Avatar>
                                                            <Box>
                                                                <Typography variant="h6" fontWeight={900} color="#2D3748">
                                                                    {selectedStudent.firstName} {selectedStudent.lastName}
                                                                </Typography>
                                                                <Typography variant="subtitle2" color="#4A5568" fontWeight={700}>ID: {selectedStudent.studentId}</Typography>
                                                                <Typography variant="caption" color="text.secondary" fontWeight={600}>Grade: {selectedStudent.grade} | Class: {selectedStudent.class}</Typography>
                                                            </Box>
                                                        </Stack>
                                                    </Paper>
                                                </Grid>
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    <Box sx={{ p: 3, textAlign: 'right' }}>
                                                        <Typography variant="h4" fontWeight={900} color="#263B7C">{reportCardData.summary.overallPercentage}%</Typography>
                                                        <Typography variant="subtitle1" fontWeight={900} color={reportCardData.summary.overallPercentage >= 50 ? '#48BB78' : '#E53E3E'}>
                                                            GRADE: {reportCardData.summary.grade}
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight={800} color="#4A5568">
                                                            RANK: {reportCardData.summary.rank} of {reportCardData.summary.classSize}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', textTransform: 'uppercase' }}>Performance Score</Typography>
                                                    </Box>
                                                </Grid>
                                            </Grid>

                                            {/* Grades Table */}
                                            <Typography variant="h6" fontWeight={800} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <AssessmentIcon color="primary" /> ACADEMIC TRANSCRIPT
                                            </Typography>
                                            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: '16px', mb: 4 }}>
                                                <Table>
                                                    <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                                                        <TableRow>
                                                            <TableCell sx={{ fontWeight: 800 }}>SUBJECT</TableCell>
                                                            <TableCell align="center" sx={{ fontWeight: 800 }}>RAW SCORE</TableCell>
                                                            <TableCell align="center" sx={{ fontWeight: 800 }}>PERCENTAGE</TableCell>
                                                            <TableCell align="center" sx={{ fontWeight: 800 }}>GRADE</TableCell>
                                                            <TableCell sx={{ fontWeight: 800 }}>TEACHER FEEDBACK</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {reportCardData.grades.map((g: any) => (
                                                            <TableRow key={g.id}>
                                                                <TableCell sx={{ fontWeight: 700 }}>{g.subject?.name || 'Subject'}</TableCell>
                                                                <TableCell align="center">{g.marks} / {g.maxMarks}</TableCell>
                                                                <TableCell align="center">
                                                                    <Box sx={{ display: 'inline-block', px: 1.5, py: 0.5, borderRadius: '8px', bgcolor: '#EBF4FF', color: '#4C51BF', fontWeight: 800 }}>
                                                                        {((Number(g.marks) / Number(g.maxMarks)) * 100).toFixed(0)}%
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell align="center" sx={{ fontWeight: 900 }}>
                                                                    {getLetterGrade((g.marks / g.maxMarks) * 100, selectedStudent.grade)}
                                                                </TableCell>
                                                                <TableCell sx={{ color: 'text.secondary', fontSize: '0.875rem', fontStyle: 'italic' }}>
                                                                    "{g.comment || 'No comment provided'}"
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>

                                            {/* Remarks Section */}
                                            <Grid container spacing={3}>
                                                <Grid size={{ xs: 12 }}>
                                                    <Typography variant="subtitle1" fontWeight={900} sx={{ mb: 1, textTransform: 'uppercase', color: '#263B7C', letterSpacing: 1 }}>Official Remarks</Typography>
                                                    <Stack spacing={2}>
                                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px' }}>
                                                            <Typography variant="caption" fontWeight={800} color="#263B7C">TEACHER'S GENERAL COMMENT</Typography>
                                                            <Typography variant="body2">{reportCardData.termReport?.teacherComment || 'Pending assessment'}</Typography>
                                                        </Paper>
                                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px', bgcolor: '#FFF5F5' }}>
                                                            <Typography variant="caption" fontWeight={800} color="#E53E3E">HOD'S COMMENT</Typography>
                                                            <Typography variant="body2">{reportCardData.termReport?.hodComment || 'Pending review'}</Typography>
                                                        </Paper>
                                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px', bgcolor: '#F0FFF4' }}>
                                                            <Typography variant="caption" fontWeight={800} color="#38A169">PRINCIPAL'S COMMENT</Typography>
                                                            <Typography variant="body2">{reportCardData.termReport?.principalComment || 'Pending sign-off'}</Typography>
                                                        </Paper>
                                                    </Stack>
                                                </Grid>

                                                <Grid size={{ xs: 12 }}>
                                                    <Divider sx={{ my: 3 }} />
                                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                        <Box>
                                                            <Typography variant="subtitle2" fontWeight={800}>Attendance Standing</Typography>
                                                            <Typography variant="h5" fontWeight={900} color="#263B7C">{reportCardData.attendance.percentage}%</Typography>
                                                            <Typography variant="caption" color="text.secondary">{reportCardData.attendance.PRESENT || 0} Days Present</Typography>
                                                        </Box>
                                                        <Box sx={{ textAlign: 'center', minWidth: 200, mt: 4 }}>
                                                            <Divider sx={{ mb: 1, borderColor: '#263B7C', borderBottomWidth: 2 }} />
                                                            <Typography variant="caption" fontWeight={800} color="text.secondary">SCHOOL STAMP & PRINCIPAL'S SIGNATURE</Typography>
                                                        </Box>
                                                        <Box sx={{ textAlign: 'right' }}>
                                                            <Typography variant="subtitle2" fontWeight={800}>Next Term Information</Typography>
                                                            <Typography variant="h5" fontWeight={900} color="#ED8936">
                                                                {reportCardData.termReport?.nextTermDate ? new Date(reportCardData.termReport.nextTermDate).toLocaleDateString() : 'TBA'}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">SCHOOL REOPENS</Typography>
                                                        </Box>
                                                    </Stack>
                                                </Grid>
                                            </Grid>

                                            <Box sx={{ mt: 10, pt: 4, borderTop: '1px solid #edf2f7', textAlign: 'center', opacity: 0.5 }}>
                                                <Typography variant="caption">
                                                    This is an official document generated by {school?.name || 'the School Management System'}. Any alteration renders it void.
                                                    Generated on {new Date().toLocaleString()}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ) : (
                                        <Typography align="center" color="text.secondary">No report card data found.</Typography>
                                    )}
                                </DialogContent>
                                <DialogActions sx={{ p: 3, bgcolor: '#F8FAFC' }} className="no-print">
                                    <Button onClick={() => setReportCardDialogOpen(false)}>Close</Button>
                                    <Button 
                                        variant="contained" 
                                        startIcon={<DownloadIcon />} 
                                        sx={{ bgcolor: '#263B7C', borderRadius: '8px' }}
                                        onClick={() => window.print()}
                                    >
                                        Download PDF / Print
                                    </Button>
                                </DialogActions>
                            </Dialog>
                            {/* FINANCIALS TAB */}
                            {activeTab === 0 && (
                                <Box>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} mb={3} spacing={2}>
                                        <Box>
                                            <Typography variant="h6" fontWeight={800} color="#263B7C">
                                                Financial Summary: {selectedStudent.firstName}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                                ID: {selectedStudent.studentId} | {selectedStudent.grade} - {selectedStudent.class}
                                            </Typography>
                                        </Box>
                                        <Button
                                            variant="contained"
                                            startIcon={<ReceiptLongIcon />}
                                            onClick={() => navigate(`/dashboard/parent-portal/statement/${selectedStudent.id}`)}
                                            sx={{ bgcolor: '#263B7C', borderRadius: '10px', px: 3, fontWeight: 700 }}
                                        >
                                            Full Statement
                                        </Button>
                                    </Stack>

                                    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #edf2f7', borderRadius: '16px' }}>
                                        <Table>
                                            <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 800, color: '#4A5568' }}>Invoice No</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, color: '#4A5568' }}>Date</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, color: '#4A5568' }}>Amount</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, color: '#4A5568' }}>Status</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 800, color: '#4A5568' }}>Action</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {selectedStudent.invoices?.length > 0 ? selectedStudent.invoices.map((inv: any) => (
                                                    <TableRow key={inv.id} sx={{ '&:last-child td': { border: 0 } }}>
                                                        <TableCell sx={{ fontWeight: 700 }}>{inv.invoiceNo}</TableCell>
                                                        <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>{new Date(inv.createdAt).toLocaleDateString()}</TableCell>
                                                        <TableCell sx={{ fontWeight: 800 }}>${Number(inv.totalAmount).toLocaleString()}</TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={inv.status.replace('_', ' ')}
                                                                size="small"
                                                                sx={{
                                                                    fontWeight: 800,
                                                                    fontSize: '0.65rem',
                                                                    bgcolor: inv.status === 'PAID' ? '#E6FFFA' : '#FFF5F5',
                                                                    color: inv.status === 'PAID' ? '#38A169' : '#E53E3E',
                                                                    borderRadius: '8px'
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Button size="small" sx={{ fontWeight: 700 }} onClick={() => navigate(`/dashboard/invoices/${inv.id}`)}>View</Button>
                                                        </TableCell>
                                                    </TableRow>
                                                )) : (
                                                    <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>No invoices found</TableCell></TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>

                                    {/* POS Transactions Section */}
                                    <Box sx={{ mt: 4 }}>
                                        <Typography variant="h6" fontWeight={800} color="#263B7C" mb={2}>
                                            Recent Tuck Shop / POS Purchases
                                        </Typography>
                                        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #edf2f7', borderRadius: '16px' }}>
                                            <Table>
                                                <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 800, color: '#4A5568' }}>Date</TableCell>
                                                        <TableCell sx={{ fontWeight: 800, color: '#4A5568' }}>Order #</TableCell>
                                                        <TableCell sx={{ fontWeight: 800, color: '#4A5568' }}>Items</TableCell>
                                                        <TableCell sx={{ fontWeight: 800, color: '#4A5568' }}>Amount</TableCell>
                                                        <TableCell sx={{ fontWeight: 800, color: '#4A5568' }}>Payment</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {selectedStudent.posOrders && selectedStudent.posOrders.length > 0 ? selectedStudent.posOrders.map((order: any) => (
                                                        <TableRow key={order.id} sx={{ '&:last-child td': { border: 0 } }}>
                                                            <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                                                {new Date(order.createdAt).toLocaleDateString()} {new Date(order.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                            </TableCell>
                                                            <TableCell sx={{ fontWeight: 700 }}>{order.orderNo}</TableCell>
                                                            <TableCell sx={{ fontWeight: 600, color: '#4A5568' }}>
                                                                {order.items?.length || 0} items
                                                            </TableCell>
                                                            <TableCell sx={{ fontWeight: 800 }}>${Number(order.totalAmount).toLocaleString()}</TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label={order.paymentMethod.replace('_', ' ')}
                                                                    size="small"
                                                                    sx={{
                                                                        fontWeight: 800,
                                                                        fontSize: '0.65rem',
                                                                        bgcolor: '#F0FFF4',
                                                                        color: '#38A169',
                                                                        borderRadius: '8px'
                                                                    }}
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    )) : (
                                                        <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>No recent purchases found</TableCell></TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                </Box>
                            )}

                            {/* ACADEMICS TAB */}
                            {activeTab === 1 && (
                                <Box>
                                    <Box sx={{ mb: 4 }}>
                                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
                                            <Box>
                                                <Typography variant="h6" fontWeight={800} color="#263B7C">
                                                    {selectedStudent.firstName}'s Classroom: {selectedStudent.classGroup?.name || 'Academic Feed'}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                                    Latest updates and academic performance summary.
                                                </Typography>
                                            </Box>
                                            
                                            {/* History Filters */}
                                            <Paper elevation={0} sx={{ p: 1.5, borderRadius: '16px', bgcolor: '#F8FAFC', border: '1px solid #edf2f7' }}>
                                                <Stack direction="row" spacing={2}>
                                                    <TextField
                                                        select
                                                        size="small"
                                                        label="Academic Year"
                                                        value={selectedYear}
                                                        onChange={(e) => setSelectedYear(Number(e.target.value))}
                                                        sx={{ minWidth: 140, bgcolor: 'white' }}
                                                    >
                                                        {[2023, 2024, 2025, 2026].map(year => (
                                                            <MenuItem key={year} value={year}>{year}</MenuItem>
                                                        ))}
                                                    </TextField>
                                                    <TextField
                                                        select
                                                        size="small"
                                                        label="Term"
                                                        value={selectedTerm}
                                                        onChange={(e) => setSelectedTerm(e.target.value)}
                                                        sx={{ minWidth: 120, bgcolor: 'white' }}
                                                    >
                                                        {['Term 1', 'Term 2', 'Term 3'].map(term => (
                                                            <MenuItem key={term} value={term}>{term}</MenuItem>
                                                        ))}
                                                    </TextField>
                                                </Stack>
                                            </Paper>
                                        </Stack>
                                    </Box>

                                    {/* Performance Overview (New Section) */}
                                     <Paper sx={{ p: 3, borderRadius: '24px', mb: 4, border: '1px solid #edf2f7', bgcolor: 'white', overflow: 'hidden' }}>
                                         <Grid container spacing={3} alignItems="center">
                                             <Grid size={{ xs: 12, md: 3 }}>
                                                 <Typography variant="caption" fontWeight={800} color="text.secondary" textTransform="uppercase">Current Academic Status</Typography>
                                                 {reportCardLoading ? (
                                                     <Box sx={{ py: 2 }}><LinearProgress sx={{ borderRadius: 2 }} /></Box>
                                                 ) : reportCardData ? (
                                                     <Stack direction="row" spacing={3} alignItems="center" mt={1}>
                                                         <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                                             <CircularProgress 
                                                                 variant="determinate" 
                                                                 value={reportCardData.summary.overallPercentage} 
                                                                 size={80} 
                                                                 thickness={6} 
                                                                 sx={{ color: '#4C51BF' }} 
                                                             />
                                                             <Box sx={{ top: 0, left: 0, bottom: 0, right: 0, position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                 <Typography variant="h6" fontWeight={900} color="#263B7C">{reportCardData.summary.grade}</Typography>
                                                             </Box>
                                                         </Box>
                                                         <Box>
                                                             <Typography variant="h4" fontWeight={900} color="#2D3748">{reportCardData.summary.overallPercentage}%</Typography>
                                                             <Typography variant="body2" color="text.secondary" fontWeight={700}>Overall Performance</Typography>
                                                         </Box>
                                                     </Stack>
                                                 ) : (
                                                     <Typography variant="body1" fontWeight={700} sx={{ mt: 1 }}>No results published yet.</Typography>
                                                 )}
                                             </Grid>
                                             <Grid size={{ xs: 12, md: 3 }} sx={{ borderLeft: '1px solid #edf2f7', pl: 3 }}>
                                                 <Typography variant="caption" fontWeight={800} color="text.secondary" textTransform="uppercase">Class Ranking</Typography>
                                                 {reportCardData && (
                                                     <Typography variant="h5" fontWeight={900} color="#4C51BF" mt={1}>
                                                         Rank: {reportCardData.summary.rank} of {reportCardData.summary.classSize}
                                                     </Typography>
                                                 )}
                                                 <Typography variant="body2" color="text.secondary" fontWeight={600}>Relative to peers.</Typography>
                                             </Grid>
                                             <Grid size={{ xs: 12, md: 2 }} sx={{ borderLeft: '1px solid #edf2f7', pl: 3 }}>
                                                 <Typography variant="caption" fontWeight={800} color="text.secondary" textTransform="uppercase">Attendance Correlation</Typography>
                                                 {reportCardData && (
                                                     <Typography variant="h5" fontWeight={900} color="#38A169" mt={1}>
                                                         {reportCardData.attendance.percentage}% Presence
                                                     </Typography>
                                                 )}
                                                 <Typography variant="body2" color="text.secondary" fontWeight={600}>Positive impact on learning.</Typography>
                                             </Grid>
                                             <Grid size={{ xs: 12, md: 2 }} sx={{ borderLeft: '1px solid #edf2f7', pl: 3 }}>
                                                 <Typography variant="caption" fontWeight={800} color="text.secondary" textTransform="uppercase">Next Term Opening</Typography>
                                                 <Typography variant="h5" fontWeight={900} color="#ED8936" mt={1}>
                                                     {reportCardData?.termReport?.nextTermDate ? new Date(reportCardData.termReport.nextTermDate).toLocaleDateString() : 'TBA'}
                                                 </Typography>
                                                 <Typography variant="body2" color="text.secondary" fontWeight={600}>{selectedTerm} - {selectedYear}</Typography>
                                             </Grid>
                                             <Grid size={{ xs: 12, md: 2 }} sx={{ textAlign: { md: 'right' } }}>
                                                 <Button 
                                                     variant="contained" 
                                                     fullWidth
                                                     sx={{ bgcolor: '#4C51BF', borderRadius: '12px', px: 4, fontWeight: 800, py: 1.5 }}
                                                     onClick={() => setReportCardDialogOpen(true)}
                                                 >
                                                     Full Report Card
                                                 </Button>
                                             </Grid>
                                         </Grid>
                                     </Paper>

                                     {/* Official Remarks Preview */}
                                     {reportCardData?.termReport && (
                                         <Paper sx={{ p: 4, borderRadius: '24px', mb: 5, border: '1px solid #edf2f7', bgcolor: '#F0FFF4' }}>
                                             <Typography variant="subtitle2" fontWeight={800} color="#22543D" mb={2}>OFFICIAL TERM REMARKS</Typography>
                                             <Grid container spacing={3}>
                                                 <Grid size={{ xs: 12, md: 4 }}>
                                                     <Typography variant="caption" fontWeight={700} color="text.secondary">TEACHER'S COMMENT</Typography>
                                                     <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>"{reportCardData.termReport.teacherComment || 'Pending assessment'}"</Typography>
                                                 </Grid>
                                                 <Grid size={{ xs: 12, md: 4 }}>
                                                     <Typography variant="caption" fontWeight={700} color="text.secondary">HOD'S COMMENT</Typography>
                                                     <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>"{reportCardData.termReport.hodComment || 'Pending review'}"</Typography>
                                                 </Grid>
                                                 <Grid size={{ xs: 12, md: 4 }}>
                                                     <Typography variant="caption" fontWeight={700} color="text.secondary">PRINCIPAL'S COMMENT</Typography>
                                                     <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>"{reportCardData.termReport.principalComment || 'Pending signature'}"</Typography>
                                                 </Grid>
                                             </Grid>
                                         </Paper>
                                     )}

                                    <Grid container spacing={4}>
                                        <Grid size={{ xs: 12, md: 7 }}>
                                            <Typography variant="subtitle1" fontWeight={800} mb={2.5} sx={{ display: 'flex', alignItems: 'center', color: '#2D3748' }}>
                                                <Box sx={{ p: 0.8, bgcolor: '#FFFBE6', color: '#B7791F', borderRadius: '8px', mr: 1.5, display: 'flex' }}>
                                                    <CalendarMonthIcon sx={{ fontSize: 18 }} />
                                                </Box>
                                                Class Announcements
                                            </Typography>
                                            <Stack spacing={2}>
                                                {selectedStudent.classGroup?.updates?.length > 0 ? selectedStudent.classGroup.updates.map((update: any) => (
                                                    <Paper key={update.id} sx={{ p: 2.5, bgcolor: '#F8FAFC', borderRadius: '16px', border: '1px solid #edf2f7' }} elevation={0}>
                                                        <Typography variant="subtitle2" fontWeight={900} color="#2D3748">{update.title}</Typography>
                                                        <Typography variant="body2" sx={{ my: 1.5, color: '#4A5568', lineHeight: 1.6 }}>{update.content}</Typography>
                                                        <Divider sx={{ my: 1.5, borderStyle: 'dashed' }} />
                                                        <Typography variant="caption" color="text.secondary" fontWeight={700}>
                                                            By {selectedStudent.classGroup.teacher.name} • {new Date(update.createdAt).toLocaleDateString()}
                                                        </Typography>
                                                    </Paper>
                                                )) : (
                                                    <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#F8FAFC', borderRadius: '16px', border: '1px solid #edf2f7' }}>
                                                        <Typography variant="body2" color="text.secondary" fontWeight={600}>No announcements today.</Typography>
                                                    </Box>
                                                )}
                                            </Stack>

                                            <Typography variant="subtitle1" fontWeight={800} mt={5} mb={2.5} sx={{ color: '#2D3748' }}>🎖️ Recent Achievements</Typography>
                                            <Stack direction="row" spacing={1.5} flexWrap="wrap">
                                                {selectedStudent.achievements?.length > 0 ? selectedStudent.achievements.map((ach: any) => (
                                                    <Chip
                                                        key={ach.id}
                                                        label={ach.badgeType.replace('_', ' ')}
                                                        sx={{
                                                            bgcolor: '#F0FFF4',
                                                            border: '1px solid #C6F6D5',
                                                            fontWeight: 800,
                                                            color: '#2F855A',
                                                            fontSize: '0.7rem',
                                                            py: 2, px: 1, borderRadius: '10px'
                                                        }}
                                                    />
                                                )) : (
                                                    <Typography variant="body2" color="text.secondary" fontWeight={500}>No badges earned yet. Learning is in progress!</Typography>
                                                )}
                                            </Stack>
                                        </Grid>

                                        <Grid size={{ xs: 12, md: 5 }}>
                                            <Typography variant="subtitle1" fontWeight={800} mb={2.5} sx={{ color: '#2D3748' }}>📝 Current Homework</Typography>
                                            <Stack spacing={2} mb={5}>
                                                {selectedStudent.classGroup?.assignments?.length > 0 ? selectedStudent.classGroup.assignments.map((task: any) => (
                                                    <Paper key={task.id} sx={{
                                                        p: 2.5,
                                                        borderLeft: '5px solid #FF5252',
                                                        borderRadius: '8px 16px 16px 8px',
                                                        bgcolor: '#FFF5F5'
                                                    }} elevation={0}>
                                                        <Typography variant="subtitle2" fontWeight={900} color="#C53030">{task.title}</Typography>
                                                        <Typography variant="caption" color="#E53E3E" fontWeight={800} display="block" mb={1.5}>
                                                            Due: {new Date(task.dueDate).toLocaleDateString()}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ color: '#742A2A', lineHeight: 1.5 }}>{task.description}</Typography>
                                                    </Paper>
                                                )) : (
                                                    <Box sx={{ p: 3, textAlign: 'center', bgcolor: '#F0FFF4', borderRadius: '16px', color: '#2F855A' }}>
                                                        <Typography variant="body2" fontWeight={700}>All caught up! No pending homework.</Typography>
                                                    </Box>
                                                )}
                                            </Stack>
                                            <EventsWidget />
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}

                            {/* ATTENDANCE TAB */}
                            {activeTab === 2 && (
                                <Box>
                                    <Typography variant="h6" fontWeight={800} color="#263B7C" mb={1}>
                                        📊 {selectedStudent?.firstName}'s Attendance Report
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" fontWeight={600} mb={3}>
                                        Your child's historical attendance record and rate.
                                    </Typography>

                                    {attendanceLoading ? (
                                        <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
                                    ) : attendanceData ? (
                                        <>
                                            {/* Summary Cards */}
                                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 4 }}>
                                                {[
                                                    { label: 'Present', value: attendanceData.summary.present, color: '#38A169', bg: '#F0FFF4', icon: <PresentIcon /> },
                                                    { label: 'Absent', value: attendanceData.summary.absent, color: '#E53E3E', bg: '#FFF5F5', icon: <AbsentIcon /> },
                                                    { label: 'Late', value: attendanceData.summary.late, color: '#D69E2E', bg: '#FFFFF0', icon: <LateIcon /> },
                                                    { label: 'Attendance Rate', value: `${attendanceData.summary.attendanceRate}%`, color: '#4C51BF', bg: '#EBF4FF', icon: <TodayIcon /> },
                                                ].map((stat, i) => (
                                                    <Paper key={i} elevation={0} sx={{ p: 2.5, borderRadius: '16px', border: '1px solid #edf2f7', bgcolor: stat.bg }}>
                                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                                            <Box sx={{ color: stat.color }}>{stat.icon}</Box>
                                                            <Box>
                                                                <Typography variant="caption" color="text.secondary" fontWeight={800}>{stat.label}</Typography>
                                                                <Typography variant="h5" fontWeight={800} color={stat.color}>{stat.value}</Typography>
                                                            </Box>
                                                        </Stack>
                                                    </Paper>
                                                ))}
                                            </Box>

                                            {/* Attendance Records */}
                                            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #edf2f7', borderRadius: '16px' }}>
                                                <Table>
                                                    <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                                                        <TableRow>
                                                            <TableCell sx={{ fontWeight: 800, color: '#4A5568' }}>Date</TableCell>
                                                            <TableCell sx={{ fontWeight: 800, color: '#4A5568' }}>Status</TableCell>
                                                            <TableCell sx={{ fontWeight: 800, color: '#4A5568' }}>Notes</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {attendanceData.records.slice(0, 20).map((rec: any, i: number) => (
                                                            <TableRow key={i} sx={{ '&:last-child td': { border: 0 } }}>
                                                                <TableCell sx={{ fontWeight: 700, color: '#2D3748' }}>
                                                                    {new Date(rec.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Chip
                                                                        label={rec.status}
                                                                        size="small"
                                                                        sx={{
                                                                            fontWeight: 800, fontSize: '0.65rem', borderRadius: '6px',
                                                                            bgcolor: rec.status === 'PRESENT' ? '#F0FFF4' : rec.status === 'ABSENT' ? '#FFF5F5' : rec.status === 'LATE' ? '#FFFFF0' : '#EBF4FF',
                                                                            color: rec.status === 'PRESENT' ? '#38A169' : rec.status === 'ABSENT' ? '#E53E3E' : rec.status === 'LATE' ? '#D69E2E' : '#4C51BF',
                                                                        }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.8rem' }}>
                                                                    {rec.notes || '—'}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                        {attendanceData.records.length === 0 && (
                                                            <TableRow><TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>No attendance records found</TableCell></TableRow>
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </>
                                    ) : (
                                        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>No attendance data available.</Typography>
                                    )}
                                </Box>
                            )}

                            {/* TIMETABLE TAB */}
                            {activeTab === 3 && (() => {
                                const DAYS = [
                                    { label: 'Monday', value: 1, emoji: '📚' },
                                    { label: 'Tuesday', value: 2, emoji: '✏️' },
                                    { label: 'Wednesday', value: 3, emoji: '🔬' },
                                    { label: 'Thursday', value: 4, emoji: '🎨' },
                                    { label: 'Friday', value: 5, emoji: '🏃' },
                                ];
                                const SUBJECT_COLORS = ['#4C51BF','#38A169','#D69E2E','#E53E3E','#3182CE','#805AD5','#DD6B20','#319795'];
                                // Build color map per subject
                                const subjectIds = Array.from(new Set(timetableData.map((s: any) => s.subjectId)));
                                const colorMap: Record<string, string> = {};
                                subjectIds.forEach((id: any, i: number) => { colorMap[id] = SUBJECT_COLORS[i % SUBJECT_COLORS.length]; });

                                return (
                                    <Box>
                                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
                                            <Box>
                                                <Typography variant="h6" fontWeight={800} color="#263B7C" mb={0.5}>
                                                    📅 {selectedStudent?.firstName}'s Weekly Timetable
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                                    Class: {selectedStudent?.classGroup?.name || `Grade ${selectedStudent?.grade} ${selectedStudent?.class}`} — Use this to prepare books and materials at home.
                                                </Typography>
                                            </Box>
                                        </Stack>

                                        {timetableLoading ? (
                                            <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
                                        ) : timetableData.length === 0 ? (
                                            <Box sx={{ textAlign: 'center', py: 6, bgcolor: '#F7FAFC', borderRadius: '16px', border: '1px dashed #E2E8F0' }}>
                                                <TimetableIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                                                <Typography variant="body1" fontWeight={700} color="text.secondary">No timetable set up yet.</Typography>
                                                <Typography variant="body2" color="text.secondary">Contact the school to get the timetable configured.</Typography>
                                            </Box>
                                        ) : (
                                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 2 }}>
                                                {DAYS.map(day => {
                                                    const daySlots = timetableData
                                                        .filter((s: any) => s.dayOfWeek === day.value)
                                                        .sort((a: any, b: any) => a.startTime.localeCompare(b.startTime));
                                                    return (
                                                        <Paper key={day.value} sx={{ borderRadius: '18px', border: '1px solid #edf2f7', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                                                            <Box sx={{ p: 1.5, background: 'linear-gradient(135deg, #263B7C 0%, #4C51BF 100%)', textAlign: 'center' }}>
                                                                <Typography variant="caption" fontWeight={900} color="rgba(255,255,255,0.8)" textTransform="uppercase" letterSpacing={1}>{ day.label.slice(0,3) }</Typography>
                                                                <Typography variant="body1" fontWeight={800} color="white">{ day.emoji } { day.label }</Typography>
                                                            </Box>
                                                            <Box sx={{ p: 1.5, minHeight: 100 }}>
                                                                {daySlots.length === 0 ? (
                                                                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', textAlign: 'center', pt: 2, opacity: 0.5 }}>No classes</Typography>
                                                                ) : (
                                                                    <Stack spacing={1}>
                                                                        {daySlots.map((slot: any) => {
                                                                            const color = colorMap[slot.subjectId] || '#4C51BF';
                                                                            return (
                                                                                <Paper key={slot.id} sx={{ p: 1.2, borderRadius: '10px', borderLeft: `3px solid ${color}`, bgcolor: `${color}08`, border: `1px solid ${color}20` }}>
                                                                                    <Typography variant="body2" fontWeight={800} color={color} sx={{ lineHeight: 1.2 }}>
                                                                                        {slot.subject?.name || 'Subject'}
                                                                                    </Typography>
                                                                                    <Typography variant="caption" fontWeight={700} color="#718096">
                                                                                        📖 {slot.subject?.code}
                                                                                    </Typography>
                                                                                    <Stack direction="row" spacing={0.5} alignItems="center" mt={0.5}>
                                                                                        <AccessTimeIcon sx={{ fontSize: 11, color: 'text.secondary' }} />
                                                                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                                                                            {slot.startTime}–{slot.endTime}
                                                                                        </Typography>
                                                                                    </Stack>
                                                                                </Paper>
                                                                            );
                                                                        })}
                                                                    </Stack>
                                                                )}
                                                            </Box>
                                                        </Paper>
                                                    );
                                                })}
                                            </Box>
                                        )}

                                        {/* What to bring home reminder */}
                                        {timetableData.length > 0 && (() => {
                                            const today = new Date().getDay(); // 0=Sun, 1=Mon...
                                            const tomorrowDay = today === 5 ? 1 : today === 6 ? 1 : today + 1; // wrap weekend to Monday
                                            const tomorrowSlots = timetableData.filter((s: any) => s.dayOfWeek === tomorrowDay);
                                            if (tomorrowSlots.length === 0) return null;
                                            return (
                                                <Paper sx={{ mt: 3, p: 3, borderRadius: '18px', bgcolor: '#FFFFF0', border: '1px solid #F6E05E' }}>
                                                    <Typography variant="subtitle1" fontWeight={800} color="#744210" mb={1.5}>
                                                        🎒 Tomorrow's Preparation Checklist
                                                    </Typography>
                                                    <Stack spacing={1}>
                                                        {tomorrowSlots
                                                            .sort((a: any, b: any) => a.startTime.localeCompare(b.startTime))
                                                            .map((slot: any) => (
                                                                <Stack key={slot.id} direction="row" spacing={1.5} alignItems="center">
                                                                    <MenuBookIcon sx={{ fontSize: 18, color: '#D69E2E' }} />
                                                                    <Typography variant="body2" fontWeight={700} color="#744210">
                                                                        {slot.subject?.name} <Typography component="span" variant="caption" color="#975A16" fontWeight={600}>({slot.startTime}–{slot.endTime})</Typography>
                                                                    </Typography>
                                                                </Stack>
                                                            ))}
                                                    </Stack>
                                                </Paper>
                                            );
                                        })()}
                                    </Box>
                                );
                            })()}
                        </>
                    ) : (
                        activeTab < 4 && <Typography align="center" color="text.secondary">Select a student to view details.</Typography>
                    )}

                    {/* SECURITY TAB */}
                    {activeTab === 4 && (
                        <Box sx={{ maxWidth: 500, mx: 'auto', py: 2 }}>
                            <Typography variant="h6" fontWeight={800} color="#263B7C" mb={1}>Account Security</Typography>
                            <Typography variant="body2" color="text.secondary" mb={4} fontWeight={600}>Update your portal access password.</Typography>

                            {passMessage.text && (
                                <Alert severity={passMessage.type as any} sx={{ mb: 3, borderRadius: '12px', fontWeight: 700 }}>{passMessage.text}</Alert>
                            )}

                            <Stack spacing={3}>
                                <TextField
                                    label="Current Password"
                                    type="password"
                                    fullWidth
                                    variant="outlined"
                                    value={passwordData.currentPassword}
                                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                                />
                                <TextField
                                    label="New Password"
                                    type="password"
                                    fullWidth
                                    variant="outlined"
                                    value={passwordData.newPassword}
                                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                                />
                                <TextField
                                    label="Confirm New Password"
                                    type="password"
                                    fullWidth
                                    variant="outlined"
                                    value={passwordData.confirmPassword}
                                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                                />
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={handleChangePassword}
                                    sx={{ bgcolor: '#263B7C', borderRadius: '12px', py: 1.5, fontWeight: 800, boxShadow: '0 10px 20px rgba(38, 59, 124, 0.2)' }}
                                >
                                    Update Password
                                </Button>
                            </Stack>
                        </Box>
                    )}
                </Box>
            </Paper>
        </Box>
    );
};

export default ParentPortalPage;
